import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './EmailBodyEditor.css';

/**
 * Dual-mode HTML body editor: visual (contenteditable), raw HTML, preview.
 * @param {string} value - HTML string
 * @param {(html: string) => void} onChange
 * @param {boolean} [disabled]
 * @param {string} [placeholder]
 * @param {number} [contentSyncKey] - increment when parent replaces HTML (e.g. merge preview) so Visual updates even if focused
 */
function EmailBodyEditor({
  value,
  onChange,
  disabled = false,
  placeholder = 'Write your message…',
  contentSyncKey = 0,
}) {
  const [tab, setTab] = useState('visual'); // visual | raw | preview
  const editorRef = useRef(null);
  const visualFocused = useRef(false);
  const prevContentSyncKey = useRef(contentSyncKey);

  const pushHtml = useCallback(
    (html) => {
      if (disabled) return;
      onChange(html || '');
    },
    [onChange, disabled]
  );

  // Parent-driven full replace (merge preview, template load): always repaint visual DOM.
  useEffect(() => {
    if (tab !== 'visual' || !editorRef.current) return;
    if (contentSyncKey !== prevContentSyncKey.current) {
      prevContentSyncKey.current = contentSyncKey;
      editorRef.current.innerHTML = value || '';
      visualFocused.current = false;
    }
  }, [contentSyncKey, value, tab]);

  // Sync visual when value changes from parent and editor is not focused (template load, raw tab edits)
  useEffect(() => {
    if (tab !== 'visual' || !editorRef.current || visualFocused.current) return;
    const next = value || '';
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
  }, [value, tab]);

  const prevTab = useRef(null);
  useLayoutEffect(() => {
    if (tab === 'visual' && editorRef.current && prevTab.current !== 'visual') {
      editorRef.current.innerHTML = value || '';
    }
    prevTab.current = tab;
  }, [tab, value]);

  const exec = (command, arg = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    try {
      document.execCommand(command, false, arg);
    } catch {
      /* ignore */
    }
    if (editorRef.current) {
      pushHtml(editorRef.current.innerHTML);
    }
  };

  const handleVisualInput = () => {
    if (!editorRef.current || disabled) return;
    pushHtml(editorRef.current.innerHTML);
  };

  const handleLink = () => {
    if (disabled) return;
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL (https://…)', 'https://');
    if (url) exec('createLink', url);
  };

  return (
    <div className="email-body-editor">
      <div className="email-body-editor-tabs" role="tablist" aria-label="Body editor mode">
        {['visual', 'raw', 'preview'].map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={
              tab === id
                ? 'email-body-editor-tab email-body-editor-tab--active'
                : 'email-body-editor-tab'
            }
            onClick={() => setTab(id)}
            disabled={disabled}
          >
            {id === 'visual' ? 'Visual' : id === 'raw' ? 'Raw HTML' : 'Preview'}
          </button>
        ))}
      </div>

      {tab === 'visual' && (
        <>
          <div className="email-body-editor-toolbar" aria-label="Formatting">
            <button type="button" onClick={() => exec('bold')} disabled={disabled} title="Bold">
              <strong>B</strong>
            </button>
            <button type="button" onClick={() => exec('italic')} disabled={disabled} title="Italic">
              <em>I</em>
            </button>
            <button type="button" onClick={() => exec('underline')} disabled={disabled} title="Underline">
              U
            </button>
            <button type="button" onClick={handleLink} disabled={disabled} title="Link">
              Link
            </button>
            <button type="button" onClick={() => exec('unlink')} disabled={disabled} title="Unlink">
              Unlink
            </button>
            <button type="button" onClick={() => exec('insertUnorderedList')} disabled={disabled} title="Bullet list">
              • List
            </button>
            <button type="button" onClick={() => exec('insertOrderedList')} disabled={disabled} title="Numbered list">
              1. List
            </button>
          </div>
          <div
            ref={editorRef}
            className="email-body-editor-visual"
            contentEditable={!disabled}
            suppressContentEditableWarning
            data-placeholder={placeholder}
            onFocus={() => {
              visualFocused.current = true;
            }}
            onInput={handleVisualInput}
            onBlur={() => {
              visualFocused.current = false;
              handleVisualInput();
            }}
          />
        </>
      )}

      {tab === 'raw' && (
        <textarea
          className="email-body-editor-raw"
          value={value || ''}
          onChange={(e) => pushHtml(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          spellCheck={false}
        />
      )}

      {tab === 'preview' && (
        <div
          className="email-body-editor-preview"
          dangerouslySetInnerHTML={{
            __html: (value || '').trim()
              ? value
              : '<p class="email-body-editor-preview-empty">Nothing to preview.</p>',
          }}
        />
      )}
    </div>
  );
}

export default EmailBodyEditor;
